import { Storage } from '@bihu/common-js'
import { Toast } from 'antd-mobile'
import { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import activityApi from '@/apis/activity'
import { ENTER_AGREEMENT, TOKEN } from '@/constants/storage'
import { useAppDispatch } from '@/store/hooks'
import { logout } from '@/store/user'
import { RegisterProtocol } from '@/pages/application-flow-skins'

interface ChannelDetail {

  /** 渠道详情返回的渠道码；定位接口优先使用该值。 */
  adChannelCode?: string

  /** 渠道落地类型；领取额度类型需要保留原城市排除分流。 */
  linkType?: number

  /** 是否在登录后沿用旧流程请求地理位置。 */
  location?: boolean
}

interface RegisterSubmission {

  /** 新皮肤本地交互层校验后的验证码。 */
  code: string

  /** 新皮肤本地交互层校验后的手机号。 */
  phone: string
}

interface ReverseGeoResult {

  /** 旧定位接口返回的城市排除判断结果。 */
  data: {

    /** 解析出的城市名称。 */
    city: string

    /** 是否命中旧渠道的排除城市规则。 */
    excludeCity: boolean

    /** 解析出的省份名称。 */
    province: string
  }
}

interface LoginResult {

  /** 旧登录接口返回的用户标识。 */
  loginUser: {

    /** 供旧流程继续使用的用户 ID。 */
    uid: string
  }

  /** 供旧流程继续使用的登录令牌。 */
  token: string
}

interface Options {

  /** 当前入口解析出的渠道码。 */
  adChannelCode: string

  /** 已加载的渠道详情；仅读取旧流程已使用的字段。 */
  channelDetail: ChannelDetail | undefined

  /** 旧渠道标签，用于保持返回行为兼容。 */
  channelValue: string | null

  /** 当前渠道是否已被解析为 v2 注册皮肤。 */
  isV2Register: boolean

  /** 登录或定位完成后的既有旧流程分流函数。 */
  onContinue: () => void | Promise<void>

  /** 旧注册容器是否已经结束加载并展示页面。 */
  showPage: boolean

  /** 入口类型，决定旧登录接口的 isFormReq 参数。 */
  type?: 'getQuota'
}

interface V2RegisterBusinessBridge {

  /** 保持 backRegister 渠道语义的返回动作。 */
  handleBack: () => void

  /** 写入旧协议进入标记并跳往指定协议页。 */
  openProtocol: (protocol: RegisterProtocol) => void

  /** 调用旧短信接口并在成功后缓存手机号。 */
  requestCode: (phone: string) => Promise<boolean>

  /** 调用旧登录接口，并恢复定位或后续旧流程分流。 */
  submitRegister: (submission: RegisterSubmission) => Promise<boolean>
}

const CHANNEL_LINK_TYPE_GET_QUOTA = 2
const AB_TEST_TYPE = 5
const SPECIAL_FORM_CHANNEL = 'YxNj9v'

/**
 * useV2RegisterBusinessBridge — 将新版注册皮肤的动作桥接回旧注册业务
 * @returns {V2RegisterBusinessBridge} 供新皮肤容器注入的业务回调
 */
const useV2RegisterBusinessBridge = ({
  adChannelCode,
  channelDetail,
  channelValue,
  isV2Register,
  onContinue,
  showPage,
  type,
}: Options): V2RegisterBusinessBridge => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const locationStartedRef = useRef(false)

  const handleLocationResult = useCallback(async(latitude: number | null, longitude: number | null) => {
    if (!channelDetail) {
      await onContinue()
      return
    }

    const params = latitude === null || longitude === null ? {
      adChannelCode: channelDetail.adChannelCode || adChannelCode,
    } : {
      location: `${latitude},${longitude}`,
      adChannelCode: channelDetail.adChannelCode || adChannelCode,
      coordtype: 'wgs84ll',
    }
    const res = await activityApi.reverseGeo(params) as unknown as ReverseGeoResult

    if (res.data.excludeCity && channelDetail.linkType === CHANNEL_LINK_TYPE_GET_QUOTA) {
      await activityApi.reportDevice({
        provinceName: res.data.province,
        cityName: res.data.city,
      })
      window.location.href = `/fill-information/success?options=${encodeURIComponent(JSON.stringify({
        isNotFormReq: true,
        isSpecialListCity: true,
      }))}`
      return
    }

    await onContinue()
  }, [adChannelCode, channelDetail, onContinue])

  const requestLocation = useCallback(() => {
    if (!('geolocation' in navigator)) {
      handleLocationResult(null, null)
      return
    }

    navigator.geolocation.getCurrentPosition(
      position => handleLocationResult(position.coords.latitude, position.coords.longitude),
      () => handleLocationResult(null, null),
      {
        enableHighAccuracy: true,
        timeout: 2000,
      },
    )
  }, [handleLocationResult])

  const requestCode = useCallback(async(phone: string): Promise<boolean> => {
    try {
      await activityApi.smsSend({
        mobile: +phone,
        channelCode: adChannelCode,
      })
      localStorage.setItem('user_phone', phone)
      return true
    } catch (error) {
      Toast.show('验证码发送失败，请重试')
      return false
    }
  }, [adChannelCode])

  const submitRegister = useCallback(async({ code, phone }: RegisterSubmission): Promise<boolean> => {
    Toast.show({
      icon: 'loading',
      content: '领取中…',
      duration: 0,
    })
    try {
      const res = await activityApi.login({
        mobile: phone,
        code,
        adChannelCode,
        abTestType: AB_TEST_TYPE,
        isFormReq: type === 'getQuota' && adChannelCode !== SPECIAL_FORM_CHANNEL,
      }) as unknown as LoginResult
      Storage.set('user_uid', res.loginUser.uid)
      Storage.set(TOKEN, res.token)

      // 页面跳转后可通过浏览器返回注册页，先清理无限时长 Toast，避免历史页恢复遗留遮罩。
      Toast.clear()
      if (channelDetail?.location) {
        requestLocation()
      } else {
        await onContinue()
      }
      return true
    } catch (error) {
      Toast.clear()
      return false
    }
  }, [adChannelCode, channelDetail?.location, onContinue, requestLocation, type])

  const openProtocol = useCallback((protocol: RegisterProtocol) => {
    Storage.set(ENTER_AGREEMENT, true)
    window.location.href = `/${protocol}-agreement?isShowHeader=true`
  }, [])

  const handleBack = useCallback(() => {
    if (channelValue?.includes('backRegister')) {
      dispatch(logout())
      window.location.href = `/getQuota${adChannelCode ? `?adChannelCode=${adChannelCode}` : ''}`
      return
    }

    navigate(-1)
  }, [adChannelCode, channelValue, dispatch, navigate])

  useEffect(() => {
    locationStartedRef.current = false
  }, [adChannelCode, isV2Register])

  useEffect(() => {
    if (!isV2Register || !showPage || !channelDetail?.location || !Storage.get(TOKEN) || locationStartedRef.current) {
      return
    }

    // 与旧默认皮肤保持一致：已登录且需要定位时，在页面展示后恢复城市分流。
    locationStartedRef.current = true
    requestLocation()
  }, [channelDetail?.location, isV2Register, requestLocation, showPage])

  return {
    handleBack,
    openProtocol,
    requestCode,
    submitRegister,
  }
}

export default useV2RegisterBusinessBridge
